import React, { useState } from 'react';
import { X, Bell, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Clean up notification message — strip redundant lead/score/reason prefixes
const cleanMessage = (message) => {
  if (!message) return message;
  const cleaned = message
    .replace(/[\s\-–—]*[\(\[]?\s*[Ss]core:?\s*\d+\s*\/\s*\d+\s*[\)\]]?\.?/g, '')
    .replace(/[\s\-–—]*\d+\/100\.?/g, '')
    .replace(/^Lead:\s[^)]+\)\s*/i, '')
    .replace(/^\([^)]*\)\s*/g, '')
    .replace(/^Reason:\s*/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned || message;
};

const getRelativeTime = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const priorityConfig = {
  critical: { border: 'border-l-red-500',   avatar: 'bg-red-100 text-red-700',    dot: 'bg-red-500'   },
  high:     { border: 'border-l-amber-400', avatar: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  medium:   { border: 'border-l-blue-500',  avatar: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-500'  },
  low:      { border: 'border-l-gray-300',  avatar: 'bg-gray-100 text-gray-600',   dot: 'bg-gray-400'  },
};

const FILTERS = ['All', 'Unread', 'Critical'];

export default function NotificationsModal({
  isOpen,
  onClose,
  notifications = [],
  onMarkAsRead,
  onDismiss,
  onMarkAllAsRead,
}) {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.read).length;

  const filtered = notifications.filter(n => {
    if (activeFilter === 'Unread')   return !n.read;
    if (activeFilter === 'Critical') return n.priority === 'critical';
    return true;
  });

  const handleNotificationClick = (notification) => {
    onMarkAsRead(notification.id);
    onClose();
    navigate(`/lead/${notification.lead_id}`);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[9990]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Bell className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
                <p className="text-xs text-gray-500">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllAsRead}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  <Check className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex border-b border-gray-100 flex-shrink-0 px-2">
            {FILTERS.map((f) => {
              const count = f === 'Unread'   ? notifications.filter(n => !n.read).length
                          : f === 'Critical' ? notifications.filter(n => n.priority === 'critical').length
                          : notifications.length;
              return (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`relative px-4 py-3 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    activeFilter === f
                      ? 'text-indigo-600'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {f}
                  {count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      activeFilter === f
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {count}
                    </span>
                  )}
                  {activeFilter === f && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-16 px-4 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {activeFilter === 'Unread'   ? 'All caught up' :
                   activeFilter === 'Critical' ? 'No critical alerts' :
                   'No notifications'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {activeFilter === 'Unread'   ? "No unread notifications right now" :
                   activeFilter === 'Critical' ? "No critical lead escalations" :
                   "No lead escalations yet"}
                </p>
              </div>
            ) : (
              filtered.map((notification) => {
                const cfg = priorityConfig[notification.priority] || priorityConfig.low;
                const leadName = notification.leads?.name || 'Unknown Lead';
                const initials = leadName.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
                const campaignName = notification.leads?.campaigns?.name;

                return (
                  <div
                    key={notification.id}
                    className={`group relative border-l-4 ${cfg.border} border-b border-gray-50 ${
                      !notification.read ? 'bg-white' : 'bg-gray-50/40'
                    } hover:bg-indigo-50/20 transition-colors`}
                  >
                    <button
                      onClick={() => handleNotificationClick(notification)}
                      className="w-full flex items-start gap-4 px-5 py-4 text-left pr-20"
                    >
                      {/* Avatar */}
                      <div className={`w-9 h-9 rounded-full ${cfg.avatar} flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`}>
                        {initials}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'} truncate`}>
                            {leadName}
                          </span>
                          {!notification.read && (
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                          )}
                          {notification.priority === 'critical' && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-100 rounded-full font-medium flex-shrink-0">
                              Critical
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                          {cleanMessage(notification.message)}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {campaignName && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded truncate max-w-[200px]">
                              {campaignName}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {getRelativeTime(notification.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-2 transition-colors" />
                    </button>

                    {/* Hover action buttons */}
                    <div className="absolute right-10 top-4 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!notification.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                          className="p-1.5 rounded-md bg-white border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors shadow-sm"
                          title="Mark as read"
                        >
                          <Check className="w-3 h-3 text-gray-400 hover:text-green-600" />
                        </button>
                      )}
                      <button
                        onClick={(e) => onDismiss(e, notification.id)}
                        className="p-1.5 rounded-md bg-white border border-gray-200 hover:bg-red-50 hover:border-red-300 transition-colors shadow-sm"
                        title="Dismiss"
                      >
                        <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/60 flex-shrink-0">
            <p className="text-xs text-gray-400 text-center">
              Showing {filtered.length} of {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              {' · '}Refreshes every 30 seconds
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
